import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useEditMode } from '../AuthContext';
import { Menu as MenuIcon, X as CloseIcon, ChevronDown as ChevronDownIcon } from 'lucide-react';

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { editMode, toggleEditMode } = useEditMode();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [guildDropdownOpen, setGuildDropdownOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const [communityDropdownOpen, setCommunityDropdownOpen] = useState(false);
  const adminDropdownRef = useRef<HTMLDivElement>(null);
  const guildDropdownRef = useRef<HTMLDivElement>(null);
  const resourcesDropdownRef = useRef<HTMLDivElement>(null);
  const communityDropdownRef = useRef<HTMLDivElement>(null);

  // Regular navigation items (not in dropdowns)
  const navItems = [
    { path: '/', label: 'Home' },
  ];

  // Guild dropdown items
  const guildItems = [
    { path: '/about', label: 'About' },
    { path: '/roster', label: 'Roster' },
    { path: '/progress', label: 'Progress' },
    { path: '/mythicplus', label: 'Mythic+' },
  ];

  // Resources dropdown items
  const resourcesItems = [
    { path: '/raid-rules', label: 'Raid Policies' },
    { path: '/housing-map', label: 'Housing Map' },
    { path: '/professions', label: 'Professions' },
  ];

  // Community dropdown items
  const communityItems = [
    { path: '/gallery', label: 'Gallery' },
    { path: '/recruitment', label: 'Recruitment' },
    { path: '/contact', label: 'Contact' },
    { path: '/merch', label: 'Merch' },
  ];

  const adminItems = [
    { path: '/admin/applications', label: 'Applications' },
    { path: '/admin/users', label: 'User Management' },
    { path: '/admin/roster-optimizer', label: 'Roster Optimizer' },
    { path: '/admin/housing-plots', label: 'Housing Plots' },
    { path: '/admin/midnight-survey', label: 'Midnight Survey' },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isAdminPageActive = () => adminItems.some(item => isActive(item.path));
  const isGuildPageActive = () => guildItems.some(item => isActive(item.path));
  const isResourcesPageActive = () => resourcesItems.some(item => isActive(item.path));
  const isCommunityPageActive = () => communityItems.some(item => isActive(item.path));

  // Helper to close mobile menu on navigation
  const handleNavClick = () => setMobileOpen(false);

  // Close dropdowns when clicking outside (only on desktop)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target as Node)) {
        setAdminDropdownOpen(false);
      }
      if (guildDropdownRef.current && !guildDropdownRef.current.contains(event.target as Node)) {
        setGuildDropdownOpen(false);
      }
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(event.target as Node)) {
        setResourcesDropdownOpen(false);
      }
      if (communityDropdownRef.current && !communityDropdownRef.current.contains(event.target as Node)) {
        setCommunityDropdownOpen(false);
      }
    };

    // Only add the click outside handler on desktop (when mobile menu is closed)
    if ((adminDropdownOpen || guildDropdownOpen || resourcesDropdownOpen || communityDropdownOpen) && !mobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [adminDropdownOpen, guildDropdownOpen, resourcesDropdownOpen, communityDropdownOpen, mobileOpen]);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 border-b border-gray-800 w-full${mobileOpen ? ' bg-black bg-opacity-100 text-white' : ' bg-black/95 backdrop-blur-md'}`}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24 sm:h-28 lg:h-32">
            {/* Logo */}
            <Link to="/" className="flex items-center group" onClick={handleNavClick}>
              <div className="neon-split-border flex items-center justify-center overflow-hidden w-28 h-12 sm:w-36 sm:h-16 lg:w-40 lg:h-20">
                <img 
                  src="/images/Just_Vibes_Logo.svg" 
                  alt="Just Vibes Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex flex-wrap items-center space-x-6 w-full justify-end">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link-desktop ${
                    isActive(item.path) ? 'active' : ''
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Guild Dropdown */}
              <div className="relative" ref={guildDropdownRef}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setGuildDropdownOpen(!guildDropdownOpen);
                    setCommunityDropdownOpen(false);
                    setAdminDropdownOpen(false);
                  }}
                  className={`nav-link-desktop flex items-center gap-1 ${isGuildPageActive() ? 'active' : ''}`}
                  aria-expanded={guildDropdownOpen}
                  aria-haspopup="true"
                >
                  Guild
                  <ChevronDownIcon size={16} className={`transition-transform duration-200 ${guildDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {guildDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-2 z-[60] animate-fade-in">
                    {guildItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors ${
                          isActive(item.path) ? 'bg-gray-700 text-white' : ''
                        }`}
                        onClick={() => {
                          setGuildDropdownOpen(false);
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Resources Dropdown */}
              <div className="relative" ref={resourcesDropdownRef}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setResourcesDropdownOpen(!resourcesDropdownOpen);
                    setGuildDropdownOpen(false);
                    setCommunityDropdownOpen(false);
                    setAdminDropdownOpen(false);
                  }}
                  className={`nav-link-desktop flex items-center gap-1 ${isResourcesPageActive() ? 'active' : ''}`}
                  aria-expanded={resourcesDropdownOpen}
                  aria-haspopup="true"
                >
                  Resources
                  <ChevronDownIcon size={16} className={`transition-transform duration-200 ${resourcesDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {resourcesDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-2 z-[60] animate-fade-in">
                    {resourcesItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors ${
                          isActive(item.path) ? 'bg-gray-700 text-white' : ''
                        }`}
                        onClick={() => {
                          setResourcesDropdownOpen(false);
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                    {user && (
                      <a
                        href="https://docs.google.com/forms/d/e/1FAIpQLSdoAGUnhOWbAuRzUqlCQXMrdi5Fq-f7l4nq_Eratf3_5XwtJA/viewform"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                        onClick={() => {
                          setResourcesDropdownOpen(false);
                        }}
                      >
                        Anonymous Feedback
                      </a>
                    )}
                  </div>
                )}
              </div>

              {/* Community Dropdown */}
              <div className="relative" ref={communityDropdownRef}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCommunityDropdownOpen(!communityDropdownOpen);
                    setGuildDropdownOpen(false);
                    setResourcesDropdownOpen(false);
                    setAdminDropdownOpen(false);
                  }}
                  className={`nav-link-desktop flex items-center gap-1 ${isCommunityPageActive() ? 'active' : ''}`}
                  aria-expanded={communityDropdownOpen}
                  aria-haspopup="true"
                >
                  Community
                  <ChevronDownIcon size={16} className={`transition-transform duration-200 ${communityDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {communityDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-2 z-[60] animate-fade-in">
                    {communityItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors ${
                          isActive(item.path) ? 'bg-gray-700 text-white' : ''
                        }`}
                        onClick={() => {
                          setCommunityDropdownOpen(false);
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {user && user.role === 'admin' && (
                <div className="relative" ref={adminDropdownRef}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAdminDropdownOpen(!adminDropdownOpen);
                      setGuildDropdownOpen(false);
                      setResourcesDropdownOpen(false);
                      setCommunityDropdownOpen(false);
                    }}
                    className={`nav-link-desktop flex items-center gap-1 ${isAdminPageActive() ? 'active' : ''}`}
                    aria-expanded={adminDropdownOpen}
                    aria-haspopup="true"
                  >
                    Admin
                    <ChevronDownIcon size={16} className={`transition-transform duration-200 ${adminDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {adminDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-2 z-[60] animate-fade-in">
                      {adminItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors ${
                            isActive(item.path) ? 'bg-gray-700 text-white' : ''
                          }`}
                          onClick={() => {
                            setAdminDropdownOpen(false);
                          }}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Account and Logout buttons for logged-in users */}
              {user && (
                <div className="flex flex-col items-end ml-4">
                  <span className="text-xs text-white mb-1">
                    Logged in as: <span className="font-semibold">{user.username || user.email}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/account"
                      className="px-4 py-2 rounded-lg font-semibold text-pink-300 border border-pink-400 hover:bg-pink-900/30 transition"
                    >
                      Account
                    </Link>
                    <button
                      onClick={logout}
                      className="px-4 py-2 rounded-lg font-semibold text-cyan-300 border border-cyan-400 hover:bg-cyan-900/30 transition"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
              {!user && (
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-lg font-semibold text-cyan-300 border border-cyan-400 hover:bg-cyan-900/30 transition"
                >
                  Log in/Register
                </Link>
              )}
            </div>

            {/* Hamburger Menu (mobile) */}
            <div className="flex lg:hidden items-center">
              {/* Mobile Hamburger Icon */}
              <button
                className="flex items-center justify-center p-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-400"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
              >
                <MenuIcon size={32} className="text-cyan-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Slide-Out Menu */}
        {mobileOpen && (
          <>
            <div
              className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm transition-opacity duration-300"
              onClick={handleNavClick}
            />
            <div className="fixed top-0 left-0 bottom-0 w-64 max-w-full bg-black bg-opacity-100 text-white z-50 shadow-2xl border-r border-border flex flex-col p-6 animate-slide-in">
              <div className="flex items-center justify-between mb-8">
                <span className="text-xl font-bold gradient-text">Menu</span>
                <button
                  className="p-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  onClick={handleNavClick}
                  aria-label="Close menu"
                >
                  <CloseIcon size={28} className="text-cyan-400" />
                </button>
              </div>
              <nav className="flex flex-col space-y-4">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-link-mobile ${isActive(item.path) ? 'active' : ''}`}
                    onClick={handleNavClick}
                  >
                    {item.label}
                  </Link>
                ))}
                
                {/* Guild Dropdown (Mobile) */}
                <div className="space-y-2">
                  <button
                    onClick={() => setGuildDropdownOpen(!guildDropdownOpen)}
                    className={`nav-link-mobile flex items-center justify-between w-full ${isGuildPageActive() ? 'active' : ''}`}
                  >
                    Guild
                    <ChevronDownIcon size={16} className={`transition-transform ${guildDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {guildDropdownOpen && (
                    <div className="ml-4 space-y-2">
                      {guildItems.map((item) => (
                        <button
                          key={item.path}
                          className={`nav-link-mobile text-sm text-left w-full ${isActive(item.path) ? 'active' : ''}`}
                          onClick={() => {
                            setGuildDropdownOpen(false);
                            setMobileOpen(false);
                            navigate(item.path);
                          }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Resources Dropdown (Mobile) */}
                <div className="space-y-2">
                  <button
                    onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
                    className={`nav-link-mobile flex items-center justify-between w-full ${isResourcesPageActive() ? 'active' : ''}`}
                  >
                    Resources
                    <ChevronDownIcon size={16} className={`transition-transform ${resourcesDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {resourcesDropdownOpen && (
                    <div className="ml-4 space-y-2">
                      {resourcesItems.map((item) => (
                        <button
                          key={item.path}
                          className={`nav-link-mobile text-sm text-left w-full ${isActive(item.path) ? 'active' : ''}`}
                          onClick={() => {
                            setResourcesDropdownOpen(false);
                            setMobileOpen(false);
                            navigate(item.path);
                          }}
                        >
                          {item.label}
                        </button>
                      ))}
                      {user && (
                        <button
                          className="nav-link-mobile text-sm text-left w-full"
                          onClick={() => {
                            setResourcesDropdownOpen(false);
                            setMobileOpen(false);
                            window.open(
                              'https://docs.google.com/forms/d/e/1FAIpQLSdoAGUnhOWbAuRzUqlCQXMrdi5Fq-f7l4nq_Eratf3_5XwtJA/viewform',
                              '_blank',
                              'noopener,noreferrer'
                            );
                          }}
                        >
                          Anonymous Feedback
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Community Dropdown (Mobile) */}
                <div className="space-y-2">
                  <button
                    onClick={() => setCommunityDropdownOpen(!communityDropdownOpen)}
                    className={`nav-link-mobile flex items-center justify-between w-full ${isCommunityPageActive() ? 'active' : ''}`}
                  >
                    Community
                    <ChevronDownIcon size={16} className={`transition-transform ${communityDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {communityDropdownOpen && (
                    <div className="ml-4 space-y-2">
                      {communityItems.map((item) => (
                        <button
                          key={item.path}
                          className={`nav-link-mobile text-sm text-left w-full ${isActive(item.path) ? 'active' : ''}`}
                          onClick={() => {
                            setCommunityDropdownOpen(false);
                            setMobileOpen(false);
                            navigate(item.path);
                          }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {user && user.role === 'admin' && (
                  <div className="space-y-2">
                    <button
                      onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                      className={`nav-link-mobile flex items-center justify-between w-full ${isAdminPageActive() ? 'active' : ''}`}
                    >
                      Admin
                      <ChevronDownIcon size={16} className={`transition-transform ${adminDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {adminDropdownOpen && (
                      <div className="ml-4 space-y-2">
                        {adminItems.map((item) => (
                          <button
                            key={item.path}
                            className={`nav-link-mobile text-sm text-left w-full ${isActive(item.path) ? 'active' : ''}`}
                            onClick={() => {
                              // Close both dropdowns first
                              setAdminDropdownOpen(false);
                              setMobileOpen(false);
                              // Navigate programmatically
                              navigate(item.path);
                            }}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {user ? (
                  <div className="flex flex-col space-y-2 mt-4 pt-4 border-t border-gray-700">
                    <span className="text-xs text-cyan-300 mb-1">
                      Logged in as: <span className="text-pink-400 font-semibold">{user.username || user.email}</span>
                    </span>
                    <div className="flex flex-col gap-2">
                      <Link
                        to="/account"
                        className="px-4 py-2 rounded-lg font-semibold text-cyan-300 border border-cyan-400 hover:bg-cyan-900/30 transition text-center"
                        onClick={handleNavClick}
                      >
                        Account
                      </Link>
                      <button
                        onClick={() => { logout(); handleNavClick(); }}
                        className="px-4 py-2 rounded-lg font-semibold text-pink-300 border border-pink-400 hover:bg-pink-900/30 transition"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className={`nav-link-mobile ${isActive('/login') || isActive('/register') ? 'active' : ''}`}
                    onClick={handleNavClick}
                  >
                    Log in/Register
                  </Link>
                )}
              </nav>
            </div>
            {/* Slide-in animation */}
            <style>{`
              @keyframes slide-in {
                from { transform: translateX(-100%); }
                to { transform: translateX(0); }
              }
              .animate-slide-in {
                animation: slide-in 0.3s cubic-bezier(0.4,0,0.2,1) forwards;
              }
            `}</style>
          </>
        )}
      </nav>
      {/* Floating Edit Mode Toggle for Admins */}
      {user && user.role === 'admin' && (
        <button
          className={`fixed z-50 bottom-6 right-6 md:bottom-8 md:right-8 bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 transition-all duration-200 ${editMode ? 'ring-4 ring-cyan-300' : ''}`}
          style={{ fontWeight: 700, fontSize: '1rem' }}
          onClick={toggleEditMode}
        >
          {editMode ? 'Editing' : 'Edit Mode'}
          <span className="ml-1">{editMode ? '🖉' : '👁️'}</span>
        </button>
      )}
    </>
  );
};

export default Navbar; 